<template>
  <NuxtLayout name="default">
    <div v-if="error" class="error-wrapper">
      <error404
        v-if="Number(error.statusCode) === 404"
        :error="error"
        :contact-us-link="contactUsLink"
      />
      <error500
        v-else-if="Number(error.statusCode) === 500"
        :error="error"
        :contact-us-link="contactUsLink"
      />
      <error-general v-else :error="error" :contact-us-link="contactUsLink" />
      <div v-if="isDev">
        <h2>Error Details</h2>
        <pre>{{ error.message }}</pre>
        <pre>{{ error.stack }}</pre>
        <pre>{{
          typeof error === 'object' && error !== null
            ? JSON.stringify(error, null, 2)
            : String(error)
        }}</pre>
      </div>
    </div>
    <nuxt-to-meta :nuxt2meta="head" />
  </NuxtLayout>
</template>

<script lang="ts">
import { Component, Prop, Vue, toNative } from '~/utility/pu-class-decorator'

@Component
class ErrorRoot extends Vue {
  get head() {
    return {
      meta: [
        {
          hid: 'robots',
          name: 'robots',
          content: 'noindex',
        },
      ],
    }
  }

  @Prop({ type: Object, required: true }) readonly error!: NuxtError & {
    httpHeaders?: Record<string, string>
  }

  // Note: httpHeaders handling was removed as it's not used in Nuxt 3
  // The error page should not manipulate response headers

  mounted() {
    // Send non-404 errors to Sentry
    this.sendErrorAndOpenFeedbackModal()
    this.togglePageWrapperClass(true)
  }

  beforeUnmount() {
    if (this && this.togglePageWrapperClass) {
      this.togglePageWrapperClass(false)
    }
  }

  get contactUsLink() {
    // Defensive: $i18n may not be initialized if hydration fails
    const locale = this.$i18n?.locale ?? 'en-gb'
    return `https://www.pitchup.com/${
      locale !== 'en-gb' ? locale + '/' : ''
    }about/contact/`
  }

  private get shouldCaptureToSentry(): boolean {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- NuxtError type has statusCode
    const statusCode = Number(this.error.statusCode)

    // Don't capture 404s - these are expected
    if (statusCode === 404) {
      return false
    }

    // Capture all other errors (500+, etc.)
    return true
  }

  private get isActualException(): boolean {
    // Check if this is a real Error/exception with stack trace
    // vs a synthetic error event or browser Event
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- Dynamic error type checking
    const err = this.error as any

    // Browser Event objects should not be treated as exceptions
    if (err instanceof Event) {
      return false
    }

    // Check for browser Event-like properties
    /* eslint-disable @typescript-eslint/no-unsafe-member-access -- Dynamic error type checking */
    if (
      err.type &&
      (err.target || err.currentTarget || err.eventPhase !== undefined)
    ) {
      return false
    }
    /* eslint-enable @typescript-eslint/no-unsafe-member-access */

    // If it's an Error instance with a stack trace, it's an exception
    if (err instanceof Error && err.stack) {
      return true
    }

    // If it has a stack trace, treat it as an exception
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- Dynamic error type checking for non-Error objects
    return Boolean(err.stack)
  }

  private sendErrorAndOpenFeedbackModal() {
    if (!this.shouldCaptureToSentry) {
      // 404s are expected, no need to log
      return
    }

    const sentryReadyInstance = this.$sentry
    if (!sentryReadyInstance) {
      // Sentry not available, can't capture error
      return
    }

    let eventId: string | undefined

    // Use captureException for real exceptions, captureMessage for other errors
    if (this.isActualException) {
      // It's a real Error object - use captureException
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- Sentry captureException returns string
      eventId = sentryReadyInstance.captureException(this.error)
    } else {
      // It's a synthetic error or event - use captureMessage
      /* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- NuxtError type access */
      const message =
        this.error.message ||
        `HTTP ${this.error.statusCode} Error: ${this.error.statusMessage || 'No details'}`
      /* eslint-enable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access */
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- Sentry captureMessage returns string
      eventId = sentryReadyInstance.captureMessage(message, {
        level: 'error',
        contexts: {
          errorDetails: {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- NuxtError type access
            statusCode: this.error.statusCode,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- NuxtError type access
            statusMessage: this.error.statusMessage,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- NuxtError type access
            url: this.error.url,
          },
        },
      })
    }

    // Show feedback dialog for all errors that were captured to Sentry
    // If the user is seeing an error page, we want their feedback
    if (eventId) {
      // @ts-expect-error -- showReportDialog exists on Sentry but types don't include it
      sentryReadyInstance.showReportDialog({ eventId })
    }
  }

  private togglePageWrapperClass(hasWrapper: boolean) {
    const pageWrapperEl = document.querySelector('#page-wrapper')
    if (!pageWrapperEl) {
      return
    }

    if (hasWrapper) {
      pageWrapperEl.classList.add('error-page-wrapper')
    } else {
      pageWrapperEl.classList.remove('error-page-wrapper')
    }
  }

  get isDev() {
    return process.env.NODE_ENV !== 'production'
  }
}

export default toNative(ErrorRoot)
</script>

<style scoped>
.error-wrapper {
  line-height: 1.4;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  font-size: var(--body);
  justify-content: flex-start;
  padding: 1.4em;
}

.error-wrapper :deep(.header) {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin: 0 0 1em 0;
}

.error-wrapper :deep(.header h2) {
  margin: 0 0 0 0.5em;
  color: var(--colour-blue);
}

.error-wrapper :deep(.error > a) {
  display: block;
  margin-top: 1em;
}
</style>

<style>
.error-page-wrapper {
  min-height: 100vh !important;
}
</style>
